import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft } from 'lucide-react';
import {
  Heading,
  Text,
  Stack,
} from '@/components/ui/index.js';
import { SportCard } from '../SportCard/SportCard.jsx';
import styles from './SportSelectionStep.module.css';

/**
 * Sports data with icons and labels
 */
const SPORTS = [
  { id: 'basketball', label: 'Basketball', icon: 'dribbble' },
  { id: 'soccer', label: 'Soccer', icon: 'circle' },
  { id: 'tennis', label: 'Tennis', icon: 'circle-dot' },
  { id: 'volleyball', label: 'Volleyball', icon: 'triangle' },
  { id: 'running', label: 'Running', icon: 'person-standing' },
  { id: 'cycling', label: 'Cycling', icon: 'bike' },
  { id: 'swimming', label: 'Swimming', icon: 'waves' },
  { id: 'yoga', label: 'Yoga', icon: 'flower-2' },
  { id: 'golf', label: 'Golf', icon: 'target' },
  { id: 'baseball', label: 'Baseball', icon: 'pentagon' },
];

/**
 * SportSelectionStep - Sport preference selection
 * 
 * Features:
 * - Grid of selectable sport cards
 * - Multi-select capability
 * - Staggered entrance animations
 * - Disabled continue until at least one sport selected
 */
export function SportSelectionStep({
  selectedSports,
  onSportToggle,
  onContinue,
  onBack,
}) {
  const hasSelection = selectedSports.length > 0;

  return (
    <Stack direction="column" gap={6} fullHeight>
      {/* Header */}
      <Stack direction="column" gap={3}>
        <button
          type="button"
          onClick={onBack}
          className={styles.backButton}
          aria-label="Go back"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>

        <Stack direction="column" gap={2}>
          <Heading level={2} size="lg" color="primary" align="left">
            What sports do you play?
          </Heading>
          <Text size="base" color="secondary" align="left">
            Select all that apply. You can change this later.
          </Text>
        </Stack>
      </Stack>

      {/* Sport Grid */}
      <div className={styles.grid}>
        {SPORTS.map((sport, index) => (
          <SportCard
            key={sport.id}
            id={sport.id}
            label={sport.label}
            icon={sport.icon}
            isSelected={selectedSports.includes(sport.id)}
            onToggle={() => onSportToggle(sport.id)}
            animationDelay={index * 50}
          />
        ))}
      </div>

      {/* Continue Button */}
      <div className={styles.footer}>
        <button
          type="button"
          onClick={onContinue}
          disabled={!hasSelection}
          className={`${styles.continueButton} ${hasSelection ? styles.continueButtonActive : ''}`}
        >
          Continue
        </button>
      </div>
    </Stack>
  );
}

SportSelectionStep.propTypes = {
  selectedSports: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSportToggle: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default SportSelectionStep;
